import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { eventAPI, reminderAPI, analyticsAPI } from '../services/api';
import { useAppStore } from '../stores/appStore';
import { Event, EventStatus } from '../types';
import { format, isToday, startOfDay, endOfDay } from 'date-fns';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Query keys
const eventKeys = {
  all: ['events'] as const,
  lists: () => [...eventKeys.all, 'list'] as const,
  list: (filters: any) => [...eventKeys.lists(), filters] as const,
  details: () => [...eventKeys.all, 'detail'] as const,
  detail: (id: string) => [...eventKeys.details(), id] as const,
  live: () => [...eventKeys.all, 'live'] as const,
  byDate: (date: string) => [...eventKeys.all, 'by-date', date] as const,
};

export const useEvents = (filters?: {
  date?: Date;
  category?: string;
  status?: EventStatus;
}) => {
  const { t } = useTranslation();
  const setEvents = useAppStore(state => state.setEvents);
  const setLoading = useAppStore(state => state.setLoading);

  const dateFilter = filters?.date ? format(filters.date, 'yyyy-MM-dd') : undefined;

  const query = useQuery({
    queryKey: eventKeys.list({ date: dateFilter, category: filters?.category, status: filters?.status }),
    queryFn: async () => {
      setLoading(true);
      try {
        const data = await eventAPI.getEvents({
          date: dateFilter,
          category: filters?.category,
          status: filters?.status,
        });
        setEvents(data);
        setLoading(false);
        return data;
      } catch (error) {
        console.error('Failed to fetch events:', error);
        setLoading(false);
        toast.error(t('errors.network'));
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    events: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useEventsByDate = (date: Date) => {
  const { t } = useTranslation();
  const setEvents = useAppStore(state => state.setEvents);
  const filterEventsByDate = useAppStore(state => state.filterEventsByDate);

  const dateStr = format(date, 'yyyy-MM-dd');

  const query = useQuery({
    queryKey: eventKeys.byDate(dateStr),
    queryFn: async () => {
      try {
        const data = await eventAPI.getEvents({ date: dateStr });
        setEvents(data);
        filterEventsByDate(date);
        return data;
      } catch (error) {
        console.error('Failed to fetch events by date:', error);
        toast.error(t('errors.network'));
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000,
  });

  return {
    events: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useEvent = (eventId: string) => {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: eventKeys.detail(eventId),
    queryFn: async () => {
      try {
        return await eventAPI.getEvent(eventId);
      } catch (error) {
        console.error('Failed to fetch event:', error);
        toast.error(t('errors.notFound'));
        throw error;
      }
    },
    enabled: !!eventId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    event: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useLiveEvents = () => {
  const { t } = useTranslation();

  const query = useQuery({
    queryKey: eventKeys.live(),
    queryFn: async () => {
      try {
        return await eventAPI.getLiveEvents();
      } catch (error) {
        console.error('Failed to fetch live events:', error);
        return [];
      }
    },
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    staleTime: 15 * 1000, // 15 seconds
  });

  return {
    liveEvents: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useReminder = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toggleReminder = useAppStore(state => state.toggleReminder);
  const isReminderSet = useAppStore(state => state.isReminderSet);

  const setReminderMutation = useMutation({
    mutationFn: reminderAPI.setReminder,
    onSuccess: (_, variables) => {
      toggleReminder(variables.eventId);
      toast.success(t('reminders.added'));
      // Track analytics
      analyticsAPI.trackEventInteraction(variables.eventId, 'remind');
    },
    onError: (error) => {
      console.error('Failed to set reminder:', error);
      toast.error(t('errors.generic'));
    },
  });

  const removeReminderMutation = useMutation({
    mutationFn: (eventId: string) => reminderAPI.removeReminder(eventId),
    onSuccess: (_, eventId) => {
      toggleReminder(eventId);
      toast.success(t('reminders.removed'));
    },
    onError: (error) => {
      console.error('Failed to remove reminder:', error);
      toast.error(t('errors.generic'));
    },
  });

  const handleToggleReminder = async (eventId: string, minutesBefore: number = 5) => {
    if (isReminderSet(eventId)) {
      removeReminderMutation.mutate(eventId);
    } else {
      // Check and request notification permission before setting reminder
      if ('Notification' in window) {
        if (Notification.permission === 'denied') {
          toast.error(t('notifications.permissionDenied'));
          return;
        }
        
        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') {
            toast.error(t('notifications.permissionRequired'));
            return;
          }
        }
      }
      
      setReminderMutation.mutate({
        eventId,
        minutesBefore,
        enabled: true,
      });
    }
  };

  return {
    toggleReminder: handleToggleReminder,
    isReminderSet,
    isLoading: setReminderMutation.isLoading || removeReminderMutation.isLoading,
  };
};

export const useEventInteraction = () => {
  const trackInteraction = (eventId: string, action: 'view' | 'remind' | 'share' | 'favorite') => {
    analyticsAPI.trackEventInteraction(eventId, action).catch(console.error);
  };

  return { trackInteraction };
};

// Custom hook for filtering and sorting events
export const useEventFilters = (events: Event[]) => {
  const filteredEvents = useAppStore(state => state.filteredEvents);
  const getEventsByStatus = useAppStore(state => state.getEventsByStatus);
  const getUpcomingEvents = useAppStore(state => state.getUpcomingEvents);
  const getActiveEvents = useAppStore(state => state.getActiveEvents);
  const isEventActive = useAppStore(state => state.isEventActive);
  const getEventProgress = useAppStore(state => state.getEventProgress);

  const filterByStatus = (status: EventStatus) => {
    return events.filter(event => {
      if (status === 'active') return isEventActive(event);
      return event.status === status;
    });
  };

  const filterByCategory = (category: string) => {
    return events.filter(event => event.category === category);
  };

  const filterByToday = () => {
    return events.filter(event => isToday(new Date(event.startTime)));
  };

  const sortByStartTime = (ascending = true) => {
    return [...events].sort((a, b) => {
      const dateA = new Date(a.startTime).getTime();
      const dateB = new Date(b.startTime).getTime();
      return ascending ? dateA - dateB : dateB - dateA;
    });
  };

  return {
    filteredEvents,
    getEventsByStatus,
    getUpcomingEvents,
    getActiveEvents,
    isEventActive,
    getEventProgress,
    filterByStatus,
    filterByCategory,
    filterByToday,
    sortByStartTime,
  };
};